import {
  amplitudeSendEvent,
  amplitudeSendScreenEvent,
  GTM_NOTSET_INTEGER,
  GTM_NOTSET_STRING,
  getGtmAttr,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import type { CouponGoodsItemDto } from '@musinsa/services-curator';
import { getScreenNameWithFallback } from '../../common/utils/getScreenNameWithFallback';

export const SCREEN_COUPON_GOODS_OWNER = 'screen_curator_coupons_goods';
export const SCREEN_COUPON_GOODS_VISITOR = 'screen_curator_coupons_goods_visit';

// NOTE: Phase2 신규 페이지(시트 4·5). mss 페어리티 베이스 없음 — AMP+GA4 로그 정의서 spec 그대로 발화.
// GA4 section_name 은 curator_coupon_title (PLP 상단 영역) — 상품 ecommerce(view_item_list/select_item)
// 는 별도 item_list_id=curator_coupon_goods 로 컨테이너 data attribute 발화.

interface CouponMeta {
  brandId: string;
  couponId: number;
  couponName: string;
  /** GA4 content_type 으로 사용되는 쿠폰 할인율 (예: "20"). */
  couponType: string;
  curatorId: string;
  /** AMP `from` 의 fallback screen name (본/방 공용 함수가 본/방 분기에 사용). */
  fromScreen: string;
}

interface CouponProductMeta extends CouponMeta {
  productId: number;
  productName: string;
}

interface CouponProductImpressionMeta extends CouponMeta {
  productId: number;
}

const SECTION_COUPON_GOODS = 'curator_coupons_goods';
const SECTION_COUPON_GOODS_LIST = 'curator_coupons_goods_list';
const GA4_SECTION_COUPON_TITLE = 'curator_coupon_title';

/** 시트 4 row 11 — 큐레이터 쿠폰 적용 상품 페이지(본인) 진입 */
export function trackOwnerCouponGoodsScreenViewMusinsa(params: CouponMeta): void {
  amplitudeSendScreenEvent({
    action_name: 'screen',
    brand_id: params.brandId,
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'screen_curator_coupons_goods',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    note: SECTION_COUPON_GOODS,
    section_name: SECTION_COUPON_GOODS,
  });
}

/** 시트 5 row 11 — 큐레이터 쿠폰 적용 상품 페이지(방문) 진입 */
export function trackVisitorCouponGoodsScreenViewMusinsa(params: CouponMeta): void {
  amplitudeSendScreenEvent({
    action_name: 'screen',
    brand_id: params.brandId,
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'screen_curator_coupons_goods_visit',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    note: SECTION_COUPON_GOODS,
    section_name: SECTION_COUPON_GOODS,
  });
}

/** 시트 4/5 row 12 — 큐레이션 샵 클릭 (본인/방문 공용) + GA4 2605 시트 row 3. */
export function trackCouponGoodsCuratorShopClickMusinsa(params: CouponMeta): void {
  amplitudeSendEvent({
    action_name: 'click',
    brand_id: params.brandId,
    button_name: 'curator_shop',
    component_name: 'button',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'click_button',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    section_name: SECTION_COUPON_GOODS,
  });

  pushDataLayer('click_content', {
    brand_id: params.brandId,
    content_id: String(params.couponId),
    content_name: params.couponName,
    content_profile_id: params.curatorId,
    content_type: params.couponType,
    extra_info: 'curator_shop',
    index: GTM_NOTSET_STRING,
    section_index: '3',
    section_name: GA4_SECTION_COUPON_TITLE,
  });
}

/**
 * 시트 4 row 13 — 공유 링크 복사 (본인 전용) + GA4 2605 시트 row 4.
 * 시트 5 row 13 은 시트 작성자 카피페이스트 실수로 동일 button_name=copy_link 가 박혀 있었으나
 * 실제 방문 페이지의 secondary CTA 액션은 "쿠폰 받기" 라 button_name='coupon_download' 가 정답
 * (`trackVisitorCouponGoodsCouponDownloadClickMusinsa` 분리). 본 함수는 본인 페이지에서만 사용.
 */
export function trackCouponGoodsCopyLinkClickMusinsa(params: CouponMeta): void {
  amplitudeSendEvent({
    action_name: 'click',
    brand_id: params.brandId,
    button_name: 'copy_link',
    component_name: 'button',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'click_button',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    section_name: SECTION_COUPON_GOODS,
  });

  pushDataLayer('click_content', {
    brand_id: params.brandId,
    content_id: String(params.couponId),
    content_name: params.couponName,
    content_profile_id: params.curatorId,
    content_type: params.couponType,
    extra_info: 'copy_link',
    index: GTM_NOTSET_STRING,
    section_index: '4',
    section_name: GA4_SECTION_COUPON_TITLE,
  });
}

/**
 * 시트 5 row 13 (방문) — 쿠폰 받기 클릭. button_name='coupon_download' (mss `coupon_download` 페어리티).
 * 시트 spec 의 button_name=copy_link 는 카피페이스트 실수로 확인 — 실제 액션과 페어리티 베이스 기준으로 분리.
 * GA4 2605 시트 row 4 (방문 쿠폰 받기) 동시 발화 — extra_info='coupon_download'.
 */
export function trackVisitorCouponGoodsCouponDownloadClickMusinsa(params: CouponMeta): void {
  amplitudeSendEvent({
    action_name: 'click',
    brand_id: params.brandId,
    button_name: 'coupon_download',
    component_name: 'button',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'click_button',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    section_name: SECTION_COUPON_GOODS,
  });

  pushDataLayer('click_content', {
    brand_id: params.brandId,
    content_id: String(params.couponId),
    content_name: params.couponName,
    content_profile_id: params.curatorId,
    content_type: params.couponType,
    extra_info: 'coupon_download',
    index: GTM_NOTSET_STRING,
    section_index: '4',
    section_name: GA4_SECTION_COUPON_TITLE,
  });
}

/**
 * GA4 2605 시트 row 2 — 쿠폰 적용 상품 페이지(본/방 공용) > 쿠폰명 타이틀 노출 (impression_content).
 * `impression_content` 는 `pushDataLayer` 미지원 — `getGtmAttr` 으로 노출 영역 div 의
 * className + data-* attribute 로 인코딩해 GTM 이 viewport 진입 시 자동 발화.
 * 콜사이트에서 `{ className, ...rest }` 분리해 className 은 className 으로, 나머지 data-*
 * 속성은 spread 해야 GTM 측 handler 가 attribute 를 정상 read 한다.
 */
export function buildCouponGoodsTitleImpressionGtmAttr(params: CouponMeta) {
  return getGtmAttr(['impression_content'], {
    brand_id: params.brandId,
    content_id: String(params.couponId),
    content_name: params.couponName,
    content_profile_id: params.curatorId,
    content_type: params.couponType,
    index: GTM_NOTSET_STRING,
    section_index: '2',
    section_name: GA4_SECTION_COUPON_TITLE,
  });
}

/**
 * GA4 2605 시트 row 5(view_item_list)·row 6(select_item) — PLP 상품 노출/클릭 ecommerce.
 * 모노레포 다른 PLP (buildSelectItemGtmAttr 등) 와 동일한 attr 기반 발화 컨벤션 유지.
 * 반환 객체의 `className` + 나머지 `data-*` 속성을 모두 root element 에 박아야 GTM 매칭 정상.
 * spec: `item_list_id=curator_coupon_goods`, `item_extra_info={coupon_name}_{coupon_no}`.
 */
export function buildCouponGoodsItemGtmAttr(
  item: CouponGoodsItemDto,
  index: number,
  coupon: { couponId: number; couponName: string },
) {
  const originalPrice = item.normalPrice;
  const couponPrice = item.couponPrice;
  // NOTE: spec 시트의 `item_extra_info` 는 EcommerceEventParameter 타입에 미정의 — 임의 키
  // 허용을 위해 cast. data-* 변환은 getGtmAttr 내부에서 모든 키 동일하게 처리.
  const payload = {
    discount:
      originalPrice != null && item.price != null ? originalPrice - item.price : GTM_NOTSET_INTEGER,
    discount_rate: item.salesRate?.toString() ?? GTM_NOTSET_STRING,
    item_brand: item.brandName ?? GTM_NOTSET_STRING,
    item_category: GTM_NOTSET_STRING,
    item_id: String(item.goodsNo),
    item_list_id: 'curator_coupon_goods',
    item_list_index: String(index),
    item_extra_info: `${coupon.couponName}_${coupon.couponId}`,
    original_price: originalPrice ?? GTM_NOTSET_INTEGER,
    price: item.price ?? GTM_NOTSET_INTEGER,
    quantity: 1,
    ...(couponPrice != null ? { best_price: couponPrice } : {}),
  } as Parameters<typeof getGtmAttr<'select_item' | 'view_item_list'>>[1];
  return getGtmAttr(['select_item', 'view_item_list'], payload);
}

/** 시트 4/5 row 14 — PLP 상품 노출 (본인/방문 공용) */
export function trackCouponGoodsProductImpressionMusinsa(
  params: CouponProductImpressionMeta,
): void {
  amplitudeSendEvent({
    action_name: 'impression',
    brand_id: params.brandId,
    component_name: 'content',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'impression_content',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    product_id: String(params.productId),
    section_name: SECTION_COUPON_GOODS_LIST,
  });
}

/** 시트 4 row 15 — PLP 상품 클릭 (본인) component_name=product */
export function trackOwnerCouponGoodsProductClickMusinsa(params: CouponProductMeta): void {
  amplitudeSendEvent({
    action_name: 'click',
    brand_id: params.brandId,
    component_name: 'product',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'click_product',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    product_id: String(params.productId),
    product_name: params.productName,
    section_name: SECTION_COUPON_GOODS_LIST,
  });
}

/** 시트 5 row 15 — PLP 상품 클릭 (방문) component_name=content (텍사노미상 콘텐츠 영역으로 분류) */
export function trackVisitorCouponGoodsProductClickMusinsa(params: CouponProductMeta): void {
  amplitudeSendEvent({
    action_name: 'click',
    brand_id: params.brandId,
    component_name: 'content',
    coupon_id: String(params.couponId),
    coupon_name: params.couponName,
    event_name: 'click_content',
    from: getScreenNameWithFallback(params.fromScreen),
    id: params.curatorId,
    product_id: String(params.productId),
    product_name: params.productName,
    section_name: SECTION_COUPON_GOODS_LIST,
  });
}
